import React, { useState, useImperativeHandle, forwardRef, useCallback, useEffect } from 'react';

import { format } from 'date-fns';
import { ZfBox, ZfInput, ZfRadio, ZfSelectionControlGroup, ZfSelectOption, ZfStack } from '@zafire/react';
import { useCaretResetOnFocus } from 'hooks/useCaretResetOnFocus';

/**
 * AgGrid Custom Filter Props Interface
 */
interface IPartnerColumnFilterProps {
  filterChangedCallback?: () => void;
  api?: any;
  columnApi?: any;
  column?: any;
  registerFilter?: (filterInstance: any) => void;
}

interface IDateFilter {
  dateFrom: string;
  dateTo: string | null;
  condition: string;
}

type LogicalOperator = 'AND' | 'OR';

export const DateFilterList = [
  { label: 'Equals', value: 'equals' },
  { label: 'Before', value: 'lessThan' },
  { label: 'After', value: 'greaterThan' },
  { label: 'Between', value: 'inRange' },
];

const DateFilter = forwardRef<any, IPartnerColumnFilterProps>((props, ref) => {
  // const [dateFrom, setDateFrom] = useState<string >('');
  // const [dateTo, setDateTo] = useState<string | null>(null);
  // const [selectedCondition, setSelectedCondition] = useState<string>('equals');
  const [dateFilterList, setDateFilterList] = useState<IDateFilter[]>([
    { dateFrom: '', dateTo: null, condition: 'equals' },
  ]);
  const [conditionOperator, setConditionOperator] = useState<LogicalOperator>('AND');
  const dateFieldCaretReset = useCaretResetOnFocus();

  useEffect(() => {
    if (dateFilterList.length === 1 && (dateFilterList[0].dateFrom !== '' || dateFilterList[0].dateTo !== null)) {
      setDateFilterList([...dateFilterList, { dateFrom: '', dateTo: null, condition: 'equals' }]);
    } else if (
      dateFilterList.length === 2 &&
      dateFilterList[1].dateFrom === '' &&
      dateFilterList[1].dateTo === null &&
      dateFilterList[0].dateFrom === '' &&
      dateFilterList[0].dateTo === null
    ) {
      setDateFilterList([dateFilterList[0]]);
    }
  }, [dateFilterList]);

  // Register filter instance with parent component
  useEffect(() => {
    if (props.registerFilter) {
      // Pass the current component instance
      props.registerFilter({
        setModel: (model: any) => {
          if (model === null || model === undefined) {
            setDateFilterList([{ dateFrom: '', dateTo: null, condition: 'equals' }]);
          }
        },
      });
    }
  }, [props]);

  const updateFilter = useCallback(() => {
    if (props.api && props.column) {
      try {
        // Get current filter model
        const currentFilterModel = props.api.getFilterModel() || {};

        // Build new filter model for this column
        const columnId = props.column.getColId();
        const newFilterModel = { ...currentFilterModel };

        if (dateFilterList[1].dateFrom === '' && dateFilterList[1].dateTo === null) {
          const { dateFrom, condition, dateTo } = dateFilterList[0];

          if (dateFrom) {
            if (condition === 'inRange' && dateTo === null) {
              return;
            }
            // Set filter for this column
            newFilterModel[columnId] = {
              filterType: 'date',
              type: condition,
              dateFrom: format(new Date(dateFrom), 'yyyy-MM-dd 00:00:00'), //replicating ag-Grid format
              dateTo: dateTo ? format(new Date(dateTo), 'yyyy-MM-dd 00:00:00') : null, //replicating ag-Grid format
            };
          } else {
            // Remove filter for this column
            delete newFilterModel[columnId];
          }
        } else {
          const conditions = dateFilterList.map((df) => ({
            filterType: 'date',
            type: df.condition,
            dateFrom: format(new Date(df.dateFrom), 'yyyy-MM-dd 00:00:00'),
            dateTo: df.dateTo ? format(new Date(df.dateTo), 'yyyy-MM-dd 00:00:00') : null,
          }));

          newFilterModel[columnId] = {
            operator: conditionOperator,
            conditions: conditions,
            filterType: 'date',
          };
        }

        // Apply the new filter model
        props.api.setFilterModel(newFilterModel);

        // Now trigger the refresh
        if (props.api.refreshServerSide) {
          props.api.refreshServerSide({ purge: true });
        } else if (props.api.onFilterChanged) {
          props.api.onFilterChanged();
        }
        if (props.api.hideColumnFilter) {
          props.api.hideColumnFilter();
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[DateColumnFilter] Error setting filter model:', error);
      }
    }
  }, [props.api, props.column, dateFilterList, conditionOperator]);

  const resetFilter = useCallback(() => {
    if (props.api && props.column) {
      try {
        // Get current filter model
        const currentFilterModel = props.api.getFilterModel() || {};

        // Build new filter model for this column
        const columnId = props.column.getColId();
        const newFilterModel = { ...currentFilterModel };

        // Remove filter for this column
        delete newFilterModel[columnId];

        // Apply the new filter model
        props.api.setFilterModel(newFilterModel);

        // Now trigger the refresh
        if (props.api.refreshServerSide) {
          props.api.refreshServerSide({ purge: true });
        } else if (props.api.onFilterChanged) {
          props.api.onFilterChanged();
        }
        if (props.api.hideColumnFilter) {
          props.api.hideColumnFilter();
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[DateColumnFilter] Error setting filter model:', error);
      }
    }
  }, [props.api, props.column, dateFilterList, conditionOperator]);

  // Expose AG Grid filter methods
  useImperativeHandle(ref, () => ({
    doesFilterPass() {
      return true;
    },

    isFilterActive() {
      return dateFilterList.some((df) => df.dateFrom !== '' && df.dateFrom !== null);
    },

    getModel() {
      const { dateFrom, condition } = dateFilterList[0];

      if (dateFrom === '' || dateFrom === null) {
        return null;
      }
      return {
        filterType: 'date',
        type: condition,
        dateFrom: dateFrom,
      };
    },

    setModel(model: any) {
      if (model && model.value) {
        // eslint-disable-next-line no-console
        console.log('Setting model in DateFilter:', model);
        // setDateFrom(model.value);
      } else {
        setDateFilterList([{ dateFrom: '', dateTo: null, condition: 'equals' }]);
      }
      // setValue(model ? parse(model.value, 'yyyy-MM-dd', new Date()) : null);
    },
  }));

  const updateCondition = useCallback(
    (index: number, condition: string) => {
      setDateFilterList((prev) => prev.map((df, i) => (i === index ? { ...df, condition } : df)));
    },
    [dateFilterList],
  );

  const updateDateFrom = useCallback(
    (index: number, dateFrom: string) => {
      setDateFilterList((prev) => prev.map((df, i) => (i === index ? { ...df, dateFrom } : df)));
    },
    [dateFilterList],
  );

  const updateDateTo = useCallback(
    (index: number, dateTo: string | null) => {
      setDateFilterList((prev) => prev.map((df, i) => (i === index ? { ...df, dateTo } : df)));
    },
    [dateFilterList],
  );

  return (
    <div className="ag-filter-wrapper" style={{ width: '100%' }}>
      {dateFilterList.map((df, index) => (
        <div key={`date-filter-${index}`}>
          <div className="ag-filter-body-wrapper ag-simple-filter-body-wrapper ag-focus-managed">
            {index === 1 && (
              <ZfSelectionControlGroup
                selectionType="radio"
                value={conditionOperator}
                onGroupValueChange={(e) => {
                  setConditionOperator(e.detail as LogicalOperator);
                }}
              >
                <ZfStack direction="row" spacing="small">
                  <ZfRadio value="AND" label="AND"></ZfRadio>
                  <ZfRadio value="OR" label="OR"></ZfRadio>
                </ZfStack>
              </ZfSelectionControlGroup>
            )}
            <ZfInput
              type="select"
              onSelectChange={(e) => updateCondition(index, e.detail as string)}
              value={df.condition}
              getOptionLabel={(co) => DateFilterList.find((c) => c.value === co)?.label || ''}
            >
              {DateFilterList.map((co) => {
                return (
                  <ZfSelectOption key={co.value} value={co.value} isSelected={df.condition === co.value}>
                    {co.label}
                  </ZfSelectOption>
                );
              })}
            </ZfInput>
          </div>
          <ZfBox
            styles={{
              paddingLeft: 'var(--gap-horizontal-sm)',
              paddingRight: 'var(--gap-horizontal-sm)',
              paddingBottom: 'var(--gap-vertical-sm)',
            }}
          >
            <ZfInput
              type="date"
              dateCalendarProps={{ variant: 'day' }}
              value={df.dateFrom || ''}
              onInputChange={(e) => {
                updateDateFrom(index, e.detail);
              }}
              {...dateFieldCaretReset}
            />
          </ZfBox>
          {df.condition === 'inRange' && (
            <ZfBox
              styles={{
                paddingLeft: 'var(--gap-horizontal-sm)',
                paddingRight: 'var(--gap-horizontal-sm)',
                paddingBottom: 'var(--gap-vertical-sm)',
              }}
            >
              <ZfInput
                type="date"
                dateCalendarProps={{ variant: 'day' }}
                value={df.dateTo || ''}
                onInputChange={(e) => {
                  updateDateTo(index, e.detail);
                }}
                {...dateFieldCaretReset}
              />
            </ZfBox>
          )}
        </div>
      ))}

      <div className="ag-filter-apply-panel">
        <button
          className="ag-button ag-standard-button ag-filter-apply-panel-button ag-filter-apply-panel-apply-button"
          data-ref="applyFilterButton"
          type="submit"
          tabIndex={0}
          onClick={() => {
            updateFilter();
          }}
        >
          Apply
        </button>
        <button
          className="ag-button ag-standard-button ag-filter-apply-panel-button"
          data-ref="resetFilterButton"
          type="button"
          tabIndex={0}
          onClick={() => {
            setDateFilterList([{ dateFrom: '', dateTo: null, condition: 'equals' }]);
            resetFilter();
          }}
        >
          Reset
        </button>
      </div>
    </div>
  );
});

export default DateFilter;
